import glifyModule from 'leaflet.glify'
import type { Glify, GlifyLayer } from 'leaflet.glify'
import type { LeafletHitRouter } from './leaflet-hit-router.js'

type GlifyLike = Glify & {
  default?: Glify
  glify?: Glify
}

function looksLikeGlify(value: unknown): value is Glify {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    typeof (value as Glify).points === 'function' &&
    typeof (value as Glify).lines === 'function' &&
    typeof (value as Glify).shapes === 'function'
  )
}

/** Resolve the leaflet.glify singleton across CJS/ESM interop shapes. */
export function getLeafletGlify(): Glify {
  const root = glifyModule as unknown as GlifyLike
  const candidates: unknown[] = [
    root,
    root?.default,
    root?.glify,
    (root?.default as GlifyLike | undefined)?.glify,
  ]
  for (const candidate of candidates) {
    if (looksLikeGlify(candidate)) {
      if (typeof candidate.longitudeFirst === 'function') {
        candidate.longitudeFirst()
      }
      return candidate
    }
  }
  throw new Error('leaflet.glify: could not resolve glify API from module export')
}

/**
 * leaflet.glify draws a weighted line by stamping it. Above zoom 18 it skips
 * that brush and emits one 1px GL line. Reporting zoom 18 keeps the brush;
 * the canvas still passes the real scale, so the stroke stays the same width.
 */
const GLIFY_LINE_BRUSH_MAX_ZOOM = 18

export function glifyLineViewKeepingBrush<T extends { zoom: number }>(view: T): T {
  if (view.zoom > GLIFY_LINE_BRUSH_MAX_ZOOM) {
    return { ...view, zoom: GLIFY_LINE_BRUSH_MAX_ZOOM }
  }
  return view
}

export function keepGlifyLineBrush(layer: GlifyLayer): void {
  const drawable = layer as GlifyLayer & {
    drawOnCanvas?: ((view: { zoom: number }) => unknown) & { __gpxBrush?: boolean }
  }
  const draw = drawable.drawOnCanvas
  if (typeof draw !== 'function' || draw.__gpxBrush) return
  const wrapped = function (this: unknown, view: { zoom: number }) {
    return draw.call(this, glifyLineViewKeepingBrush(view))
  } as typeof draw
  wrapped.__gpxBrush = true
  drawable.drawOnCanvas = wrapped
}

/** Glify canvases cover the map. Pointer events stay on the vector canvas and the hit router. */
export function silenceGlifyPointer(layer: GlifyLayer): void {
  const canvas = (layer as GlifyLayer & { canvas?: HTMLCanvasElement }).canvas
  if (canvas) canvas.style.pointerEvents = 'none'
}

/**
 * Glify calls `hover` then `hoverOff` in the same turn when the cursor moves
 * from one feature to another. The extra tick in a microtask keeps the pointer
 * until a later turn where nothing hovers.
 */
export function glifyHoverCursor(
  getRouter: () => LeafletHitRouter | null,
  name: string,
): { hover: () => boolean; hoverOff: () => void } {
  let ticks = 0
  return {
    hover() {
      ticks += 1
      queueMicrotask(() => {
        ticks += 1
        getRouter()?.setHover(name, true)
      })
      return false
    },
    hoverOff() {
      const seen = ticks
      queueMicrotask(() => {
        if (ticks === seen) getRouter()?.setHover(name, false)
      })
    },
  }
}
